import { useEffect } from 'react';
import NavigationBanner from './components/NavigationBanner';
import GlassCard from './components/GlassCard';
import SpotlightCard from './components/ui/SpotlightCard';
import LiquidBackground from './components/LiquidBackground';
import { Button } from './components/ui/button';
import ProgressBar from './components/ProgressBar';
import FadeIn from './components/FadeIn';
import AIChat from './components/AIChat';

export default function App() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = [
    {
      title: 'Buzshoes.ca E-commerce',
      description:
        'Responsive e-commerce storefront built for a Canadian sneaker brand.',
      highlights: [
        'Led UX research and design system implementation.',
        'Integrated Shopify APIs for secure checkout and inventory sync.',
        'Delivered complete stack including hosting and training in 8 weeks.',
      ],
      tags: ['Framer', 'Shopify API', 'E-commerce', 'UX Research'],
    },
    {
      title: 'GatorGather',
      description:
        'Event platform for UF students featuring real-time mapping and collaboration.',
      highlights: [
        'Led a 3-person agile team through development and launch.',
        'Implemented secure authentication and user profiles with Firebase.',
        'Integrated Google Maps API for live campus activity tracking.',
      ],
      tags: ['React', 'Firebase Auth', 'Google Maps', 'Agile'],
    },
    {
      title: 'LLM Ops Toolkit',
      description:
        'Local experimentation workflow for Gemma and Qwen models.',
      highlights: [
        'Automated local fine-tuning pipelines with PyTorch and CUDA.',
        'Built CLI workflows for orchestrating LangGraph agents.',
        'Optimized inference performance for consumer hardware.',
      ],
      tags: ['Python', 'CUDA', 'PyTorch', 'LangGraph'],
    },
  ];

  const leadershipHighlights = [
    {
      title: 'Indian Student Association @ UF',
      role: 'Event Operations | Spring 2024 - Present',
      impact:
        'Coordinating logistics for large-scale festivals and driving membership growth.',
    },
    {
      title: 'Machine Intelligence Laboratory',
      role: 'Researcher | August 2025 - Present',
      impact:
        'Collaborating on robotics and AI research projects involving embedded systems.',
    },
  ];

  const skillGroups = [
    {
      title: 'Languages',
      items: ['English', 'Hindi'],
    },
    {
      title: 'Technical Stack',
      items: ['C++', 'C', 'Python', 'Assembly', 'PyTorch', 'CUDA', 'Linux CLI'],
    },
    {
      title: 'Lab & Hardware Tools',
      items: [
        'GitHub',
        'MATLAB',
        'VHDL',
        'FPGA',
        'Autodesk Inventor',
        'Intel Quartus',
        'WSL',
      ],
    },
    {
      title: 'Interests',
      items: [
        'LLM Fine-tuning',
        'Local Inference (llama.cpp)',
        'Agentic AI (LangGraph)',
      ],
    },
  ];

  return (
    <div className="min-h-screen relative text-white">
      <ProgressBar />
      <LiquidBackground />

      {/* Navigation */}
      <div className="flex justify-center">
        <NavigationBanner />
      </div>

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 pb-12 sm:pt-32 sm:pb-20">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <FadeIn>
              <div className="mb-6">
                <span className="text-foreground/60 uppercase tracking-[0.35em] text-xs sm:text-sm">
                  Computer Engineering - University of Florida
                </span>
              </div>

              <h1 className="text-foreground mb-8 text-3xl sm:text-4xl lg:text-5xl leading-relaxed lg:leading-[1.2] font-medium">
                Building intuitive web experiences and intelligent tools.
              </h1>

              <p className="text-foreground/60 mb-10 text-lg leading-relaxed max-w-xl">
                Computer Engineering student at UF. Freelance developer and researcher at the Machine Intelligence Lab.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-[var(--color-uf-orange)] hover:bg-[var(--color-uf-orange)]/90 text-white border-0 px-6 py-6 rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/20"
                >
                  <a href="#projects">View Projects</a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="backdrop-blur-md bg-white/10 border-2 border-foreground/20 hover:bg-foreground/10 text-foreground px-6 py-6 rounded-2xl transition-all duration-300 hover:scale-105"
                >
                  <a href="mailto:mittal.vansh@ufl.edu">Email Me</a>
                </Button>
              </div>

              <div className="mt-6 text-sm text-foreground/60">
                Resume:{' '}
                <a className="text-[var(--color-uf-blue)] hover:text-[var(--color-uf-orange)] transition-colors" href="/Vansh-Mittal-Resume.docx" download>
                  Download DOCX
                </a>
              </div>
            </FadeIn>

            {/* Right Card */}
            <FadeIn delay={0.2} className="lg:mt-12">
              <SpotlightCard>
                <div className="p-8 space-y-6">
                  <div>
                    <div
                      className="h-2 w-16 rounded-full mb-4"
                      style={{
                        background: 'linear-gradient(135deg, var(--color-uf-blue) 0%, var(--color-uf-orange) 100%)',
                      }}
                    />
                    <h3 className="text-foreground text-2xl mb-2">Snapshot</h3>
                    <p className="text-foreground/70">
                      B.S. Computer Engineering - Expected May 2027
                    </p>
                  </div>

                  <p className="text-foreground/60 leading-relaxed">
                    Developing e-commerce solutions, conducting AI research, and building full-cycle products.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-foreground/60 uppercase tracking-wide mb-1">Phone</p>
                      <a className="text-[var(--color-uf-blue)] hover:text-[var(--color-uf-orange)] transition-colors" href="tel:+14077827702">
                        (407) 782-7702
                      </a>
                    </div>
                    <div>
                      <p className="text-foreground/60 uppercase tracking-wide mb-1">Email</p>
                      <a className="text-[var(--color-uf-blue)] hover:text-[var(--color-uf-orange)] transition-colors" href="mailto:mittal.vansh@ufl.edu">
                        mittal.vansh@ufl.edu
                      </a>
                    </div>
                  </div>
                </div>
              </SpotlightCard>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section id="projects" className="px-4 sm:px-6 py-16 sm:py-28">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-12">
            <h2 className="text-foreground text-3xl mb-2">Featured Projects</h2>
            <p className="text-foreground/60">
              Technical showcase of my recent work.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <FadeIn key={index} delay={index * 0.1}>
                <SpotlightCard className="overflow-hidden group h-full border-l-2 border-l-[var(--color-uf-blue)]/60" spotlightColor="rgba(74, 124, 255, 0.06)">
                  <div className="p-7 h-full flex flex-col">
                    <div className="mb-4">
                      <span className="text-[var(--color-uf-blue)] text-xs font-medium tracking-wider uppercase">
                        {project.tags[0]}
                      </span>
                    </div>
                    <h3 className="text-foreground text-xl font-medium mb-3">{project.title}</h3>
                    <p className="text-foreground/60 mb-5 leading-relaxed">{project.description}</p>
                    {project.highlights && (
                      <ul className="space-y-2.5 text-foreground/50 text-sm mb-6 flex-grow">
                        {project.highlights.map((item) => (
                          <li key={item} className="flex gap-3">
                            <span className="mt-1.5 block h-1 w-1 rounded-full bg-[var(--color-uf-blue)]/60 min-w-[4px]" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="flex flex-wrap gap-2 mt-auto pt-4 border-t border-white/[0.04]">
                      {project.tags.slice(1).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-2.5 py-1 rounded-md bg-white/[0.04] text-foreground/50 text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </SpotlightCard>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WebGPU Section */}
      <section id="webgpu" className="px-4 sm:px-6 py-16 sm:py-28">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-12">
            <h2 className="text-foreground text-3xl mb-2">WebGPU AI</h2>
            <p className="text-foreground/60">
              Run AI models locally in your browser with GPU acceleration.
            </p>
          </FadeIn>

          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <FadeIn>
              <SpotlightCard className="h-full">
                <div className="p-8 space-y-6">
                  <div>
                    <div
                      className="h-2 w-16 rounded-full mb-4"
                      style={{
                        background: 'linear-gradient(135deg, var(--color-uf-blue) 0%, var(--color-uf-orange) 100%)',
                      }}
                    />
                    <h3 className="text-foreground text-2xl mb-3">How does the regular chat work?</h3>
                  </div>

                  <div className="space-y-4 text-foreground/70">
                    <p>
                      The AI chat button you see on this site uses <span className="text-[var(--color-uf-blue)] font-medium">Groq's API</span> —
                      the industry standard for fast, reliable AI inference. Your questions are sent to powerful cloud servers that
                      run large language models and return responses in milliseconds.
                    </p>
                    <p>
                      This approach offers the best quality responses and works on any device, making it ideal for production use.
                    </p>
                  </div>
                </div>
              </SpotlightCard>
            </FadeIn>

            <FadeIn delay={0.2}>
              <SpotlightCard className="h-full border-l-2 border-l-[var(--color-uf-orange)]/60" spotlightColor="rgba(250, 70, 22, 0.06)">
                <div className="p-8 space-y-6">
                  <div>
                    <span className="text-[var(--color-uf-orange)] text-xs font-medium tracking-wider uppercase">
                      Experimental
                    </span>
                    <h3 className="text-foreground text-2xl mb-3 mt-2">WebGPU: Local AI Chat</h3>
                  </div>

                  <div className="space-y-4 text-foreground/70">
                    <p>
                      <span className="text-foreground font-medium">WebGPU</span> is a cutting-edge browser API that provides
                      direct access to your GPU. This enables running AI models <span className="text-[var(--color-uf-orange)] font-medium">entirely on your device</span> —
                      no server required.
                    </p>
                    <p>
                      I've built an experimental chat using <span className="font-medium">Qwen2.5-0.5B</span>, a compact but capable
                      language model that runs locally via WebGPU. The model downloads once and is cached for future use.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex gap-2">
                        <span className="text-[var(--color-uf-orange)]">✦</span>
                        <span>Complete privacy — local processing</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[var(--color-uf-orange)]">✦</span>
                        <span>Works offline after initial download</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    asChild
                    className="w-full bg-gradient-to-r from-[var(--color-uf-blue)] to-[var(--color-uf-orange)] hover:opacity-90 text-white border-0 px-6 py-6 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg"
                  >
                    <a href="/webgpu-chat">Try WebGPU Chat →</a>
                  </Button>
                </div>
              </SpotlightCard>
            </FadeIn>

            <FadeIn delay={0.4}>
              <SpotlightCard className="h-full border-l-2 border-l-[var(--color-uf-orange)]/60" spotlightColor="rgba(250, 70, 22, 0.06)">
                <div className="p-8 space-y-6">
                  <div>
                    <span className="text-[var(--color-uf-orange)] text-xs font-medium tracking-wider uppercase">
                      Experimental
                    </span>
                    <h3 className="text-foreground text-2xl mb-3 mt-2">WebGPU: Real-Time Vision</h3>
                  </div>

                  <div className="space-y-4 text-foreground/70">
                    <p>
                      Experience real-time computer vision running directly in your browser. This demo uses the <span className="text-[var(--color-uf-orange)] font-medium">YOLOv8n</span> object detection model via ONNX Runtime Web.
                    </p>
                    <p>
                      It processes your camera feed instantly to identify and classify objects with high accuracy, demonstrating the power of modern client-side AI.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex gap-2">
                        <span className="text-[var(--color-uf-orange)]">✦</span>
                        <span>Instant object detection (60+ FPS)</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="text-[var(--color-uf-orange)]">✦</span>
                        <span>Zero latency, no server processing</span>
                      </li>
                    </ul>
                  </div>

                  <Button
                    asChild
                    className="w-full bg-gradient-to-r from-[var(--color-uf-blue)] to-[var(--color-uf-orange)] hover:opacity-90 text-white border-0 px-6 py-6 rounded-2xl transition-all duration-300 hover:scale-[1.02] shadow-lg"
                  >
                    <a href="/webgpu-vision">Try Computer Vision →</a>
                  </Button>
                </div>
              </SpotlightCard>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="px-4 sm:px-6 py-16 sm:py-28">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-12">
            <h2 className="text-foreground text-3xl mb-2">About Me</h2>
            <p className="text-foreground/60">
              Education, experience, and leadership journey.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8">
            <FadeIn>
              <SpotlightCard className="h-full">
                <div className="p-8 space-y-5 h-full">
                  <h3 className="text-foreground text-2xl">Education</h3>
                  <p className="text-foreground/70">
                    University of Florida - B.S. Computer Engineering (Expected May 2027)
                  </p>
                  <ul className="space-y-3 text-foreground/70">
                    <li>Dean's Honor List (4x)</li>
                    <li>
                      Coursework: Data Structures & Algorithms, Operating Systems, Digital Logic, Microprocessors, AI Fundamentals.
                    </li>
                  </ul>
                </div>
              </SpotlightCard>
            </FadeIn>

            <FadeIn delay={0.2}>
              <SpotlightCard className="h-full">
                <div className="p-8 space-y-5 h-full">
                  <h3 className="text-foreground text-2xl">Leadership</h3>
                  <div className="space-y-4">
                    {leadershipHighlights.map((item) => (
                      <div key={item.title} className="border-l-2 border-[var(--color-uf-blue)]/30 pl-4">
                        <p className="text-foreground font-medium">{item.title}</p>
                        <p className="text-sm text-foreground/60">{item.role}</p>
                        <p className="text-foreground/70">{item.impact}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </SpotlightCard>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="px-4 sm:px-6 py-16 sm:py-28">
        <div className="max-w-7xl mx-auto">
          <FadeIn className="mb-12">
            <h2 className="text-foreground text-3xl mb-2">Technical Skills</h2>
            <p className="text-foreground/60">
              Engineering fundamentals and modern development tools.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skillGroups.map((group, index) => (
              <FadeIn key={group.title} delay={index * 0.1}>
                <SpotlightCard className="h-full">
                  <div className="p-7 space-y-4 h-full">
                    <h3 className="text-foreground text-xl">{group.title}</h3>
                    <ul className="space-y-2 text-foreground/70">
                      {group.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </SpotlightCard>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section id="contact" className="px-4 sm:px-6 py-16 sm:py-28">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <SpotlightCard>
              <div className="p-6 sm:p-12 space-y-6">
                <h2 className="text-foreground text-3xl">Let's Collaborate</h2>
                <p className="text-foreground/70 max-w-2xl mx-auto">
                  Open to internships and freelance projects. Let's build something great.
                </p>

                <div className="flex flex-wrap justify-center gap-8 text-sm text-foreground/70">
                  <div>
                    <span className="block text-foreground font-medium mb-1">Phone</span>
                    <a className="text-[var(--color-uf-blue)] hover:text-[var(--color-uf-orange)] transition-colors" href="tel:+14077827702">
                      (407) 782-7702
                    </a>
                  </div>
                  <div>
                    <span className="block text-foreground font-medium mb-1">Email</span>
                    <a className="text-[var(--color-uf-blue)] hover:text-[var(--color-uf-orange)] transition-colors" href="mailto:mittal.vansh@ufl.edu">
                      mittal.vansh@ufl.edu
                    </a>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    asChild
                    className="bg-[var(--color-uf-orange)] hover:bg-[var(--color-uf-orange)]/90 text-white border-0 px-8 py-6 rounded-2xl transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/20"
                  >
                    <a href="mailto:mittal.vansh@ufl.edu">Email Vansh</a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="backdrop-blur-md bg-white/10 border-2 border-foreground/20 hover:bg-foreground/10 text-foreground px-8 py-6 rounded-2xl transition-all duration-300 hover:scale-105"
                  >
                    <a href="/Vansh-Mittal-Resume.docx" download>
                      Download Resume
                    </a>
                  </Button>
                </div>
              </div>
            </SpotlightCard>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-foreground/10">
        <div className="max-w-7xl mx-auto text-center text-foreground/50">
          <p>(c) 2025 Vansh Mittal. Built with React, Vite, and Tailwind CSS.</p>
        </div>
      </footer>

      <AIChat />
    </div>
  );
}